import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { 
  Container, 
  Box, 
  Typography, 
  TextField, 
  Button, 
  Paper, 
  Alert,
  CircularProgress
} from '@mui/material';
import { CheckCircle, ArrowBack } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import i18n from '../i18n';
import api from '../services/apiService';
import EmailTemplateService from '../services/emailTemplateService';

const ForgotPassword: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email) {
      setError(t('auth.emailRequired'));
      return;
    }

    try {
      setIsLoading(true);
      setError(null);
      
      // Générer le template d'email dans la langue courante
      const resetLink = `${window.location.origin}/reset-password?token=PLACEHOLDER_TOKEN`;
      const emailTemplate = EmailTemplateService.generateResetPasswordTemplate({
        username: 'PLACEHOLDER_USERNAME', // Sera remplacé par le backend avec le vrai nom d'utilisateur
        resetLink: resetLink
      });
      
      await api.post('/auth/forgot-password', {
        email,
        emailTemplate: {
          subject: emailTemplate.subject,
          htmlBody: emailTemplate.htmlBody
        },
        language: i18n.language
      });
      setSuccess(true);
    } catch (err: any) {
      console.error('Forgot password error:', err);
      setError(
        err.response?.data?.detail || 
        t('auth.forgotPasswordError')
      );
    } finally {
      setIsLoading(false);
    }
  };

  if (success) {
    return (
      <Container maxWidth="sm">
        <Box 
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '100vh',
            py: 4
          }}
        >
          <Paper 
            elevation={3} 
            sx={{ 
              p: 4, 
              width: '100%', 
              borderRadius: 2,
              textAlign: 'center'
            }}
          >
            <CheckCircle 
              sx={{ 
                fontSize: 64, 
                color: 'success.main', 
                mb: 2 
              }} 
            />
            <Typography variant="h5" component="h1" gutterBottom>
              {t('auth.emailSent')}
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              {t('auth.emailSentMessage')}
            </Typography>
            <Button
              component={Link}
              to="/login"
              variant="outlined"
              startIcon={<ArrowBack />}
            >
              {t('auth.backToLogin')}
            </Button>
          </Paper>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="sm">
      <Box 
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          py: 4
        }}
      >
        <Paper 
          elevation={3} 
          sx={{ 
            p: 4, 
            width: '100%', 
            borderRadius: 2 
          }}
        >
          <Box sx={{ mb: 3, textAlign: 'center' }}>
            <Typography variant="h4" component="h1" gutterBottom>
              {t('auth.forgotPasswordTitle')}
            </Typography>
            <Typography variant="subtitle1" color="text.secondary">
              {t('auth.forgotPasswordSubtitle')}
            </Typography>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1 }}>
            <TextField
              margin="normal"
              required
              fullWidth
              id="email"
              label={t('auth.email')}
              name="email"
              autoComplete="email"
              autoFocus
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={isLoading}
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{ mt: 3, mb: 2, py: 1.5 }}
              disabled={isLoading}
            >
              {isLoading ? <CircularProgress size={24} /> : t('auth.sendResetEmail')}
            </Button>
            
            <Box sx={{ textAlign: 'center', mt: 2 }}>
              <Link 
                to="/login" 
                style={{ 
                  textDecoration: 'none', 
                  color: 'primary.main',
                  fontSize: '0.875rem'
                }}
              >
                {t('auth.backToLogin')}
              </Link>
            </Box>
          </Box>
        </Paper>
      </Box>
    </Container>
  );
};

export default ForgotPassword;